import { useState, lazy, Suspense } from "react";
import { WEB_URL } from "@/lib/webUrl";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { Activity, Destination } from "@ckgo2/shared/types";
import type { UseFormReturn } from "react-hook-form";
import {
  ArrowLeft,
  Save,
  Loader2,
  FileText,
  Image,
  List,
  ExternalLink,
  Type,
  Video,
  Lightbulb,
  Link2,
} from "lucide-react";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import type { ActivityFormData } from "@/components/activities/types";

import DescriptionTab from "@/components/activities/DescriptionTab";
import GalleryTab from "@/components/activities/GalleryTab";

const BasicInfoTab = lazy(() => import("@/components/activities/BasicInfoTab"));
const IncludedTagsTab = lazy(() => import("@/components/activities/IncludedTagsTab"));
const VideosTab = lazy(() => import("@/components/activities/VideosTab"));
const TipsTab = lazy(() => import("@/components/activities/TipsTab"));
const RelatedTab = lazy(() => import("@/components/activities/RelatedTab"));

const TABS = [
  { id: "basic", label: "Basic info", icon: FileText },
  { id: "description", label: "Description", icon: Type },
  { id: "gallery", label: "Gallery", icon: Image },
  { id: "included", label: "Included & Tags", icon: List },
  { id: "videos", label: "Videos & Reels", icon: Video },
  { id: "tips", label: "Tips", icon: Lightbulb },
  { id: "related", label: "Related", icon: Link2 },
];

function ActivityTabContent({
  activeTab,
  form,
  destinations,
  allActivities,
  currentId,
}: {
  activeTab: string;
  form: UseFormReturn<ActivityFormData>;
  destinations: Destination[];
  allActivities: Activity[];
  currentId?: string;
}) {
  switch (activeTab) {
    case "basic":
      return <BasicInfoTab form={form} destinations={destinations} />;
    case "description":
      return <DescriptionTab form={form} />;
    case "gallery":
      return <GalleryTab form={form} />;
    case "included":
      return <IncludedTagsTab form={form} />;
    case "videos":
      return <VideosTab form={form} />;
    case "tips":
      return <TipsTab form={form} />;
    case "related":
      return (
        <RelatedTab form={form} allActivities={allActivities.filter((a) => a.id !== currentId)} />
      );
    default:
      return null;
  }
}

function transformActivityOnLoad(a: Activity): ActivityFormData {
  return {
    title: a.title || "",
    slug: a.slug || "",
    activityType: a.activityType || "",
    difficultyLevel: a.difficultyLevel || "",
    duration: a.duration || "",
    priceFrom: a.priceFrom ?? null,
    destinationId: a.destinationId || "",
    location: a.location || "",
    featuredImage: a.featuredImage || "",
    featuredImageId: a.featuredImageId ?? null,
    shortDescription: a.shortDescription || "",
    description: a.description || "",
    galleryImages: a.galleryImages || [],
    galleryImageIds: a.galleryImageIds || [],
    included: a.included || [],
    tags: a.tags || [],
    videos: a.videos || [],
    tipsContent: a.tipsContent || "",
    tips: a.tips || [],
    testimonialQuote: a.testimonialQuote || "",
    testimonialAuthor: a.testimonialAuthor || "",
    testimonialAuthorImage: a.testimonialAuthorImage || "",
    testimonialAuthorImageId: a.testimonialAuthorImageId ?? null,
    heroTitle: a.heroTitle || "",
    heading: a.heading || "",
    socialReels: a.socialReels || [],
    relatedItems: a.relatedItems || [],
    status: a.status || "draft",
    sortOrder: a.sortOrder || 0,
    isFeatured: a.isFeatured || false,
    metaTitle: a.metaTitle || "",
    metaDescription: a.metaDescription || "",
  };
}

export default function ActivityEditPage() {
  const [activeTab, setActiveTab] = useState("basic");

  const { data: destinationsData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/admin/destinations"),
  });

  const { data: allActivitiesData } = useQuery({
    queryKey: ["activities"],
    queryFn: () => api.get<ApiResponse<Activity[]>>("/api/admin/inspirations"),
  });

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    ActivityFormData,
    Activity
  >({
    resourceKey: "activity",
    listKey: "activities",
    apiPath: "/api/admin/inspirations",
    defaultValues: {
      title: "",
      slug: "",
      activityType: "",
      difficultyLevel: "",
      duration: "",
      priceFrom: null,
      destinationId: "",
      location: "",
      featuredImage: "",
      featuredImageId: null,
      shortDescription: "",
      description: "",
      galleryImages: [],
      galleryImageIds: [],
      included: [],
      tags: [],
      videos: [],
      tipsContent: "",
      tips: [],
      testimonialQuote: "",
      testimonialAuthor: "",
      testimonialAuthorImage: "",
      testimonialAuthorImageId: null,
      heroTitle: "",
      heading: "",
      socialReels: [],
      relatedItems: [],
      status: "draft",
      sortOrder: 0,
      isFeatured: false,
      metaTitle: "",
      metaDescription: "",
    },
    navigateTo: "/inspirations",
    slugSourceField: "title",
    stayOnSave: true,
    transformOnLoad: transformActivityOnLoad,
    resetDeps: [destinationsData],
  });

  const destinations = destinationsData?.data || [];
  const allActivities = allActivitiesData?.data || [];

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">Activity not found</p>
        <Link to="/inspirations" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          Back to Inspirations
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/inspirations"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New inspiration" : "Edit inspiration"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Create a new inspiration" : `Editing: ${itemData?.title}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          {!isNew && watch("slug") && (
            <a
              href={`${WEB_URL}/inspirations/${watch("slug")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              <ExternalLink className="h-5 w-5" />
              View
            </a>
          )}
          {!isNew && (
            <select {...register("status")} className="input w-40">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          )}
          <button
            type="submit"
            form="activity-form"
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Form */}
      <form id="activity-form" onSubmit={handleSubmit(onSubmit)}>
        <Suspense
          fallback={
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
            </div>
          }
        >
          <ActivityTabContent
            activeTab={activeTab}
            form={form}
            destinations={destinations}
            allActivities={allActivities}
            currentId={itemData?.id}
          />
        </Suspense>
      </form>
    </div>
  );
}
